import React from 'react';
import logo from '../assets/logo.svg';
import DrilldownSelect from './DrilldownSelect';
import MultiSelect from './MultiSelect';

// ── Static data ───────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const YEARS = [2020, 2019, 2018, 2017];
const DATE_CHILDREN = YEARS.reduce((acc, y) => ({ ...acc, [y]: MONTHS }), {});

const PRODUCT_PARENTS = ['Bikes', 'Clothing', 'Accessories', 'Components'];
const PRODUCT_CHILDREN = {
  Bikes:        ['Mountain Bikes', 'Road Bikes', 'Touring Bikes'],
  Clothing:     ['Caps', 'Gloves', 'Jerseys', 'Shorts', 'Socks', 'Vests'],
  Accessories:  ['Bike Racks', 'Bike Stands', 'Bottles and Cages', 'Cleaners', 'Fenders', 'Helmets', 'Hydration Packs', 'Lights', 'Locks', 'Panniers', 'Pumps', 'Tires and Tubes'],
  Components:   ['Bottom Brackets', 'Brakes', 'Chains', 'Cranksets', 'Derailleurs', 'Forks', 'Handlebars', 'Headsets', 'Mountain Frames', 'Pedals', 'Road Frames', 'Saddles', 'Touring Frames', 'Wheels'],
};

const REGION_PARENTS = ['North America', 'Europe', 'Pacific'];
const REGION_CHILDREN = {
  'North America': ['United States', 'Canada'],
  'Europe':        ['United Kingdom', 'France', 'Germany'],
  'Pacific':       ['Australia'],
};

const SALESPERSONS = [
  'Michael Blythe', 'Linda Mitchell', 'Jillian Carson',
  'Garrett Vargas', 'Tsvi Reiter', 'Pamela Ansman-Wolfe',
  'Shu Ito', 'Jos\u00e9 Saraiva', 'David Campbell',
  'Tete Mensa-Annan', 'Lynn Tsoflias', 'Rachel Valdez',
  'Jae Pak', 'Ranjit Varkey Chudukatil',
];

// ── SVG icons for page nav ────────────────────────────────────────

const PageIcons = {
  overview: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  sales: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  inventory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  customers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  products: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  ),
  geography: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  trends: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
};

// ── Component ─────────────────────────────────────────────────────

const Sidebar = ({
  pages,
  activePage,
  onPageChange,
  filters,
  onFiltersChange,
  activeTrigger,
  onTriggerChange,
  applyFilters,
}) => {

  const update = (key, value) => {
    const updated = { ...filters, [key]: value };
    onFiltersChange(updated);
    applyFilters(updated);
  };

  const handlePeriodChange = (val) => {
    const updated = { ...filters, period: val, periodType: null };
    onFiltersChange(updated);
    onTriggerChange(null);
    applyFilters(updated);
  };


  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img src={logo} alt="AW" />
        </div>
        <div className="sidebar-brand">
          <span className="sidebar-brand-name">AdventureWorks</span>
          <span className="sidebar-brand-sub">Analytics</span>
        </div>
      </div>

      <div className="sidebar-slicers">
        <div className="sidebar-section">
          <div className="sidebar-section-title">Global Filters</div>

          <div className="slicer-group">
            <div className="slicer-label">Calendar Year, Month</div>
            <DrilldownSelect
              parentItems={YEARS}
              childItems={DATE_CHILDREN}
              value={filters.period}
              onChange={handlePeriodChange}
              placeholder="All Periods"
            />
          </div>

          <div className="slicer-group">
            <div className="slicer-label">Product Category</div>
            <DrilldownSelect
              parentItems={PRODUCT_PARENTS}
              childItems={PRODUCT_CHILDREN}
              value={filters.product}
              onChange={(val) => update('product', val)}
              placeholder="All Products"
            />
          </div>

          <div className="slicer-group">
            <div className="slicer-label">Region</div>
            <DrilldownSelect
              parentItems={REGION_PARENTS}
              childItems={REGION_CHILDREN}
              value={filters.region}
              onChange={(val) => update('region', val)}
              placeholder="All Regions"
            />
          </div>

          <div className="slicer-group">
            <div className="slicer-label">Salesperson</div>
            <MultiSelect
              items={SALESPERSONS}
              value={filters.salesperson}
              onChange={(val) => update('salesperson', val)}
              placeholder="All Salespersons"
            />
          </div>
        </div>
      </div>

      <div className="sidebar-section sidebar-pages-header">
        <div className="sidebar-section-title">Report Pages</div>
      </div>
      <div className="sidebar-pages">
        {pages.map((page) => (
          <div
            key={page.id}
            className={`page-nav-item ${activePage === page.id ? 'active' : ''}`}
            onClick={() => onPageChange(page.id)}
          >
            <span className="page-nav-indicator" />
            <span className="page-nav-icon">{PageIcons[page.id]}</span>
            <span>{page.label}</span>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default Sidebar;
